import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';
import { useOrganizationHierarchy } from './useOrganizationHierarchy';
import { categoriesApi } from '../api/categoriesApi';
import { emailApi } from '../api/emailApi';

export type DbTicketCategory = {
  id: string;
  organization_id: string;
  name: string;
  description: string;
  is_active: boolean;
};

export type DbEmailSetting = {
  id: string;
  organization_id: string;
  provider: string;
  client_id?: string;
  tenant_id?: string;
  client_secret?: string;
  is_active: boolean;
  host?: string;
  port?: number;
  security?: string;
  username?: string;
  password_encrypted?: string;
  from_email?: string;
  from_name?: string;
  company_logo_url?: string;
  header_html?: string;
  footer_html?: string;
};

export type DbMailbox = {
  id: string;
  email: string;
  name: string;
  department: string;
};

export type DbEmailTemplate = {
  id: string;
  name: string;
  description?: string;
  subject_template: string;
  html_body: string;
  text_body?: string;
  language?: string;
  is_active: boolean;
  event_name?: string;
  category?: string;
};

// Global in-memory state for fallback settings
const defaultCategories: DbTicketCategory[] = [
  { id: 'cat-1', organization_id: 'org-1', name: 'Hardware / Procurement', description: 'Requests for new laptops, monitors, or peripheral replacements.', is_active: true },
  { id: 'cat-2', organization_id: 'org-1', name: 'Software / Access', description: 'SaaS licensing, VPN, and directory access permissions.', is_active: true },
  { id: 'cat-3', organization_id: 'org-1', name: 'Network / Infrastructure', description: 'Office network, server provisioning, and cloud environments.', is_active: true },
  { id: 'cat-4', organization_id: 'org-1', name: 'HR / Onboarding', description: 'New employee setup, offboarding, and departmental transfers.', is_active: true },
];

const defaultEmailSetting: DbEmailSetting = {
  id: 'email-1',
  organization_id: 'org-1',
  provider: 'Microsoft 365',
  client_id: '',
  tenant_id: '',
  client_secret: '',
  is_active: false
};

const defaultMailboxes: DbMailbox[] = [
  { id: 'mbx-1', email: 'support@company.com', name: 'IT Support', department: 'IT' },
  { id: 'mbx-2', email: 'billing@company.com', name: 'Finance', department: 'Finance' },
  { id: 'mbx-3', email: 'hr@company.com', name: 'Human Resources', department: 'HR' },
];


export function useSettings() {
  const { organizations, loading: orgLoading } = useOrganizationHierarchy();
  const orgId = organizations[0]?.id;

  const [categories, setCategories] = useState<DbTicketCategory[]>([]);
  const [emailSettings, setEmailSettings] = useState<DbEmailSetting | null>(null);
  const [mailboxes, setMailboxes] = useState<DbMailbox[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<DbEmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const activeOrgId = orgId || organizations[0]?.id;
      
      // 1. Fetch categories via categoriesApi
      try {
        const catData = await categoriesApi.getCategories(activeOrgId);
        if (catData && catData.length > 0) {
          setCategories(catData as any);
        } else if (supabase) {
          const { data: directCats } = await supabase.from('ticket_categories').select('*').order('name', { ascending: true });
          if (directCats && directCats.length > 0) {
            setCategories(directCats as DbTicketCategory[]);
          } else {
            setCategories(defaultCategories);
          }
        } else {
          setCategories(defaultCategories);
        }
      } catch {
        if (supabase) {
          const { data: directCats } = await supabase.from('ticket_categories').select('*').order('name', { ascending: true });
          if (directCats && directCats.length > 0) {
            setCategories(directCats as DbTicketCategory[]);
          } else {
            setCategories(defaultCategories);
          }
        } else {
          setCategories(defaultCategories);
        }
      }

      // 2. Fetch email settings via emailApi
      try {
        const emailData = await emailApi.getEmailSettings(activeOrgId);
        if (emailData && (emailData as any).id) {
          setEmailSettings(emailData as any);
        } else if (supabase && activeOrgId) {
          const { data: directEmail } = await supabase.from('email_settings').select('*').eq('organization_id', activeOrgId).maybeSingle();
          if (directEmail) setEmailSettings(directEmail as DbEmailSetting);
          else setEmailSettings(defaultEmailSetting);
        } else {
          setEmailSettings(defaultEmailSetting);
        }
      } catch {
        if (supabase && activeOrgId) {
          const { data: directEmail } = await supabase.from('email_settings').select('*').eq('organization_id', activeOrgId).maybeSingle();
          if (directEmail) setEmailSettings(directEmail as DbEmailSetting);
          else setEmailSettings(defaultEmailSetting);
        } else {
          setEmailSettings(defaultEmailSetting);
        }
      }

      // 3. Fetch mailboxes
      if (supabase && activeOrgId) {
        const { data: mbxData, error: mbxError } = await supabase
          .from('mailboxes')
          .select('*')
          .eq('organization_id', activeOrgId);
          
        if (mbxData && !mbxError && mbxData.length > 0) {
          setMailboxes(mbxData as DbMailbox[]);
        } else {
          setMailboxes(defaultMailboxes);
        }
      } else {
        setMailboxes(defaultMailboxes);
      }

      // 4. Fetch templates via emailApi
      try {
        const tplData = await emailApi.getEmailTemplates();
        if (tplData && tplData.length > 0) {
          setEmailTemplates(tplData as any);
        } else if (supabase) {
          const { data: directTpls } = await supabase.from('email_templates').select('*');
          if (directTpls && directTpls.length > 0) setEmailTemplates(directTpls as DbEmailTemplate[]);
        }
      } catch {
        if (supabase) {
          const { data: directTpls } = await supabase.from('email_templates').select('*');
          if (directTpls && directTpls.length > 0) setEmailTemplates(directTpls as DbEmailTemplate[]);
        }
      }
    } catch (e) {
      console.error('Failed to fetch settings', e);
    } finally {
      setLoading(false);
    }
  }, [orgId, organizations]);

  // Fetch settings on load
  useEffect(() => {
    if (orgLoading) return;
    fetchSettings();
  }, [orgLoading, fetchSettings]);

  const saveCategory = async (cat: Partial<DbTicketCategory>) => {
    const activeOrgId = orgId || organizations[0]?.id;
    try {
      if (cat.id && !cat.id.startsWith('cat-')) {
        await categoriesApi.updateCategory(cat.id, {
          name: cat.name,
          description: cat.description,
          is_active: cat.is_active ?? true
        });
      } else {
        await categoriesApi.createCategory({
          organization_id: activeOrgId,
          name: cat.name || '',
          description: cat.description || '',
          is_active: cat.is_active ?? true
        });
      }
    } catch (err) {
      if (supabase) {
        if (cat.id && !cat.id.startsWith('cat-')) {
          await supabase.from('ticket_categories').update({
            name: cat.name,
            description: cat.description,
            is_active: cat.is_active ?? true
          }).eq('id', cat.id);
        } else {
          await supabase.from('ticket_categories').insert({
            organization_id: activeOrgId,
            name: cat.name || '',
            description: cat.description || '',
            is_active: cat.is_active ?? true
          });
        }
      }
    }
    await fetchSettings();
  };

  const deleteCategory = async (id: string) => {
    try {
      await categoriesApi.deleteCategory(id);
    } catch (err) {
      if (supabase) {
        await supabase.from('ticket_categories').delete().eq('id', id);
      }
    }
    await fetchSettings();
  };

  const saveEmailSettings = async (settings: Partial<DbEmailSetting>) => {
    const activeOrgId = orgId || organizations[0]?.id;
    const payload = {
      organization_id: activeOrgId,
      provider: settings.provider ?? emailSettings?.provider ?? '',
      client_id: settings.client_id ?? emailSettings?.client_id ?? '',
      tenant_id: settings.tenant_id ?? emailSettings?.tenant_id ?? '',
      client_secret: settings.client_secret ?? emailSettings?.client_secret ?? '',
      from_email: settings.from_email ?? emailSettings?.from_email ?? '',
      from_name: settings.from_name ?? emailSettings?.from_name ?? '',
      is_active: settings.is_active ?? emailSettings?.is_active ?? true
    };
    
    try {
      const data = await emailApi.saveEmailSettings(payload as any);
      if (data) setEmailSettings(data as any);
      return;
    } catch (err) {
      if (supabase) {
        let res;
        if (emailSettings?.id && !emailSettings.id.startsWith('email-')) {
          res = await supabase.from('email_settings').update(payload).eq('id', emailSettings.id).select().single();
        } else {
          res = await supabase.from('email_settings').insert([payload]).select().single();
        }
        if (res.data) {
          setEmailSettings(res.data as DbEmailSetting);
        }
      }
    }
  };

  const saveMailbox = async (mailbox: Partial<DbMailbox>) => {
    const activeOrgId = orgId || organizations[0]?.id;
    if (!supabase || !activeOrgId) return;
    const payload = {
      organization_id: activeOrgId,
      email: mailbox.email || '',
      name: mailbox.name || '',
      department: mailbox.department || ''
    };

    if (mailbox.id && !mailbox.id.startsWith('mbx-')) {
      const { data, error } = await supabase.from('mailboxes').update(payload).eq('id', mailbox.id).select().single();
      if (!error && data) {
        setMailboxes(prev => prev.map(m => m.id === mailbox.id ? (data as DbMailbox) : m));
      }
    } else {
      const { data, error } = await supabase.from('mailboxes').insert([payload]).select().single();
      if (!error && data) {
        setMailboxes(prev => [...prev.filter(m => !m.id.startsWith('mbx-')), data as DbMailbox]);
      }
    }
  };

  const deleteMailbox = async (id: string) => {
    if (!supabase) return;
    if (!id.startsWith('mbx-')) {
      await supabase.from('mailboxes').delete().eq('id', id);
    }
    setMailboxes(prev => prev.filter(m => m.id !== id));
  };

  const saveEmailTemplate = async (template: Partial<DbEmailTemplate>) => {
    const payload = {
      name: template.name || '',
      description: template.description || '',
      subject_template: template.subject_template || '',
      html_body: template.html_body || '',
      text_body: template.text_body || '',
      language: template.language || 'en',
      is_active: template.is_active ?? true
    };

    try {
      if (template.id && !template.id.startsWith('tpl-')) {
        const data = await emailApi.saveEmailTemplate({ ...payload, id: template.id });
        if (data) {
          setEmailTemplates(prev => prev.map(t => t.id === template.id ? (data as any) : t));
          return;
        }
      } else {
        const data = await emailApi.saveEmailTemplate(payload);
        if (data) {
          setEmailTemplates(prev => [...prev.filter(t => !t.id.startsWith('tpl-')), data as any]);
          return;
        }
      }
    } catch (err) {
      if (supabase) {
        if (template.id && !template.id.startsWith('tpl-')) {
          const { data } = await supabase.from('email_templates').update(payload).eq('id', template.id).select().single();
          if (data) setEmailTemplates(prev => prev.map(t => t.id === template.id ? (data as DbEmailTemplate) : t));
        } else {
          const { data } = await supabase.from('email_templates').insert([payload]).select().single();
          if (data) setEmailTemplates(prev => [...prev.filter(t => !t.id.startsWith('tpl-')), data as DbEmailTemplate]);
        }
      }
    }
  };

  const deleteEmailTemplate = async (id: string) => {
    try {
      if (!id.startsWith('tpl-')) {
        await emailApi.deleteEmailTemplate(id);
      }
    } catch (err) {
      if (supabase && !id.startsWith('tpl-')) {
        await supabase.from('email_templates').delete().eq('id', id);
      }
    }
    setEmailTemplates(prev => prev.filter(t => t.id !== id));
  };

  return {
    categories,
    emailSettings,
    mailboxes,
    emailTemplates,
    loading,
    saveCategory,
    deleteCategory,
    saveEmailSettings,
    saveMailbox,
    deleteMailbox,
    saveEmailTemplate,
    deleteEmailTemplate
  };
}
